import { WorkData } from '../types';
import { normalizeDoi } from './normalizeDoi';

type Citation = {
  name: string;
  citation: string;
};

export type ExportFormat =
  | 'APA'
  | 'MLA'
  | 'Chicago'
  | 'Vancouver'
  | 'Harvard'
  | 'IEEE'
  | 'ACS'
  | 'BibTeX'
  | 'RIS';

export const exportCitationsAsText = (
  citations: Citation[],
  format: ExportFormat,
) => {
  if (!citations.length) return;

  // Filter citations for the selected format
  const filteredCitations = citations.filter((c) => c.name === format);
  if (!filteredCitations.length) return;

  // Generate filename
  const timestamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
  const filename = `references_${format.toLowerCase()}_${timestamp}.txt`;

  // Generate file content
  const dateString = new Date().toLocaleDateString();
  let content = `References List - ${format} Format\n`;
  content += `Generated: ${dateString}\n`;
  content += `\n`;

  filteredCitations.forEach((citation, index) => {
    content += `${index + 1}. ${citation.citation}\n\n`;
  });

  // Download file
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
};

// Utility to prepare work payload from saved references
export const prepareWorksPayload = (
  savedReferences: WorkData[],
): Array<{
  title: string;
  authors: Array<{ name: string }>;
  publication: string | undefined;
  publication_year: number | undefined;
  publisher: string | undefined;
  doi: string | undefined;
  url: string | undefined;
  abstract: string | undefined;
  work_id: string;
  work_type: string | undefined;
  claim_id?: string;
  claim_text?: string;
}> => {
  return savedReferences.map((workData) => {
    const work = workData.work;
    return {
      title: work.title,
      authors: work.authors || [],
      publication: work.publication,
      publication_year: work.publicationYear,
      publisher: work.publisher,
      doi: normalizeDoi(work.doi) || undefined, // Bare DOI (strip URL prefix)
      url: work.url,
      abstract: work.abstract,
      work_id: work.id, // Send work ID to backend for biblio data lookup
      work_type: work.workType, // Lets the backend source biblio for academia works
      claim_id: workData.claim_id, // Include claim ID for grouping in BibTeX export
      claim_text: workData.claim_text, // Include claim text for BibTeX comments
    };
  });
};

// Utility to download a blob as a file
export const downloadFile = (
  blob: Blob,
  defaultFilename: string,
  contentDisposition?: string | null,
) => {
  const url = URL.createObjectURL(blob);

  // Extract filename from Content-Disposition header if available
  const filenameMatch = contentDisposition?.match(/filename="?([^"]*)"?/);
  const filename = filenameMatch?.[1] || defaultFilename;

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();

  URL.revokeObjectURL(url);
};
